import { NavBar } from '../../components/NavBar.tsx';
import { StatusPill, PriorityPill, GhostPill } from '../../components/Pill.tsx';
import type { PrayerNote } from '../../domain.ts';
import { planLabel } from '../book/shared.ts';
import { Reactions } from './Reactions.tsx';

/**
 * F1-1 — 친구가 공유한 기도 노트 (읽기 전용).
 *
 * 남의 노트라 **설정 버튼이 없습니다.** 제목 · 내용 · 상태창까지는 C3 와 같고,
 * 고칠 수 있는 것과 기도 히스토리가 빠집니다 — 히스토리는 작성자에게만
 * 보이는 것입니다.
 *
 * 진행 기록은 그대로 보여 줍니다. 그 사람이 무엇에 감사했는지가 이 노트를
 * 받은 사람이 함께 기도할 근거이기 때문입니다.
 *
 * 아래에 **이모티콘 · 코멘트**가 붙습니다 (기획서 3.5). 받은 사람이 남길 수
 * 있는 것은 그 둘뿐이고, 노트 자체는 고칠 수 없습니다.
 */

interface Props {
  note: PrayerNote;
  /** 누구의 것인지 — 「이서준」처럼 부제에 적습니다 */
  author: string;
  onBack: () => void;
}

export function SharedNote({ note, author, onBack }: Props) {
  return (
    <div className="screen">
      {/* 남의 노트라 우상단 설정 버튼 자리를 비웁니다 */}
      <NavBar
        title={note.title}
        subtitle={`${author} · ${note.category}`}
        start={{ icon: 'back', label: '돌아가기', onClick: onBack }}
      />

      <div className="screen__body">
        <div className="card card--flat">
          <div className="card__pills">
            <StatusPill status={note.status} />
            {note.priority !== 'NORMAL' && <PriorityPill priority={note.priority} />}
            <GhostPill>{planLabel(note.plan)}</GhostPill>
            {note.representative && <GhostPill>대표</GhostPill>}
          </div>
        </div>

        <div className="field">
          <span className="field__label">내용</span>
          <p className="note__text">{note.body || '내용이 없습니다.'}</p>
        </div>

        {note.progress.length > 0 && (
          <div className="field">
            <span className="field__label">기도 진행 기록</span>
            <div className="stack">
              {note.progress.map((p) => (
                <article className="card" key={p.entryId}>
                  <h3 className="card__title">{p.title}</h3>
                  <p className="card__meta">{p.date.replace(/-/g, '.')}</p>
                  {p.body !== '' && <p className="card__text">{p.body}</p>}
                </article>
              ))}
            </div>
          </div>
        )}

        <p className="hint">
          {author}의 기도 노트입니다. <b>읽기 전용</b>이고, 원본이 바뀌면 여기도 함께 바뀝니다.
        </p>

        {/* 남길 수 있는 것은 이모티콘과 코멘트뿐입니다 */}
        <Reactions noteId={note.id} />
      </div>
    </div>
  );
}
